import React, { useState } from 'react';
import PresetDndContext from '../../common/DnD/PresetDndContext';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableItem from '../../common/DnD/SortableItem';
import Icon from '../../common/Icon';
import { notify } from '../../../helpers/notifications';

const LIST = 'list';
const COUNT_TABLE = 'count_table';
const OPT_TYPES = ['checkbox', 'select', 'radio'];

const CollectionChooser = ({
  chooserState,
  insertChildAttr,
  toggleShowChooser,
}) => {
  const [collectionType, setCollectionType] = useState('');
  const [fieldCollection, setFieldCollection] = useState([]);
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fieldCollection
        .map((item) => item.token)
        .indexOf(active.id);
      const newIndex = fieldCollection
        .map((item) => item.token)
        .indexOf(over.id);
      setFieldCollection(arrayMove(fieldCollection, oldIndex, newIndex));
    }
  };
  const btnClass = (type) =>
    collectionType === type ? 'small blue selected' : 'small blue';

  const handleTypeSelection = (type) => {
    setCollectionType(type);
    setFieldCollection([]);
  };

  const handleAddToCollection = (field, attr) => {
    let token = field.id.toString() + '.' + attr;
    let item = { field, attr, token, style: '' };
    if (!fieldCollection.find((f) => f.token === token))
      setFieldCollection([...fieldCollection, item]);
  };

  const handleEditStyle = (item, style, checked) => {
    setFieldCollection((items) => {
      let index = items.findIndex((i) => i.token === item.token);
      return items.map((i, idx) => {
        if (idx === index) {
          if (checked) {
            i.style = i.style.length ? `${i.style}_${style}` : `.${style}`;
          } else if (i.style.includes('_')) {
            i.style = i.style.replace(style, '').replace('_', '');
          } else {
            i.style = '';
          }
        }
        return i;
      });
    });
  };

  const buildInsertString = (type, fieldAttrs) => {
    // fieldAttrs = [ [{token: '1234.value'}], ... ]
    let root = `{{related_form.${chooserState.selectedForm.form.id}.collect.${type}.fields.`;
    let fields = fieldAttrs.map(({ token, style }) => token + style).join(',');
    let fullToken = root + fields + '}}';
    insertChildAttr(fullToken);
    notify.success('Inserted successfully!');
  };

  return (
    <div className='child-form-chooser'>
      <button onClick={toggleShowChooser}>Form Attributes</button>
      <h4>Form: {chooserState.selectedForm.form.name}</h4>

      <strong>Display responses as:</strong>
      <button
        className={btnClass(LIST)}
        onClick={() => handleTypeSelection(LIST)}
      >
        Line-Separated List
      </button>
      <button
        className={btnClass(COUNT_TABLE)}
        onClick={() => handleTypeSelection(COUNT_TABLE)}
      >
        Option Count Table
      </button>

      {collectionType === LIST && (
        <div className='collect-list'>
          <div className='list-select'>
            {chooserState.selectedForm.fieldsets.map((fs) => (
              <div key={'fs-' + fs.fieldset.id}>
                <strong>{fs.fieldset.name}</strong>
                <ul className='orderable'>
                  {fs.fields.map((f) => (
                    <React.Fragment key={'list-' + f.id}>
                      <li onClick={() => handleAddToCollection(f, 'value')}>
                        {f.name} - value
                        {fieldCollection
                          .map((item) => item.token)
                          .includes(f.id.toString() + '.value') && (
                          <Icon icon='check' className='right' />
                        )}
                      </li>
                      {chooserState.selectedForm.metadata_entries
                        .filter((me) => me.field_id == f.id)
                        .map((me) => (
                          <li
                            key={`${f.id}-${me.id}`}
                            onClick={() => handleAddToCollection(f, me.title)}
                          >
                            {f.name} - {me.title}
                            {fieldCollection
                              .map((item) => item.token)
                              .includes(f.id.toString() + '.' + me.title) && (
                              <Icon icon='check' className='right' />
                            )}
                          </li>
                        ))}
                    </React.Fragment>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className='list-insert'>
            <button
              className='small'
              onClick={() => buildInsertString(LIST, fieldCollection)}
              disabled={!fieldCollection.length}
            >
              Insert
            </button>
            <span>
              {fieldCollection.length == 0 && 'Add fields...'}
              {fieldCollection.length > 1 ? 'Drag to Reorder' : ''}
            </span>
            <PresetDndContext handleDragEnd={handleDragEnd}>
              <ul className='orderable ui-sortable'>
                {fieldCollection.length > 0 && (
                  <SortableContext
                    strategy={verticalListSortingStrategy}
                    items={fieldCollection.map((item) => item.token)}
                  >
                    {fieldCollection.map((item) => (
                      <SortableItem key={item.token} id={item.token}>
                        <div>
                          <strong>
                            {item.field.name} - {item.attr}
                            <Icon
                              icon='xmark'
                              className='right'
                              onClick={() =>
                                setFieldCollection([
                                  ...fieldCollection.filter(
                                    (i) => i.token != item.token
                                  ),
                                ])
                              }
                            />
                          </strong>
                          <div style={{ display: 'flex' }}>
                            <div style={{ paddingRight: '1em' }}>
                              <input
                                type='checkbox'
                                id={`${item.token}-b`}
                                onChange={(e) =>
                                  handleEditStyle(
                                    item,
                                    'bold',
                                    e.target.checked
                                  )
                                }
                              />
                              <label htmlFor={`${item.token}-b`}> Bold</label>
                            </div>
                            <div>
                              <input
                                type='checkbox'
                                id={`${item.token}-i`}
                                onChange={(e) =>
                                  handleEditStyle(
                                    item,
                                    'italic',
                                    e.target.checked
                                  )
                                }
                              />
                              <label htmlFor={`${item.token}-i`}> Italic</label>
                            </div>
                          </div>
                        </div>
                      </SortableItem>
                    ))}
                  </SortableContext>
                )}
              </ul>
            </PresetDndContext>
          </div>
        </div>
      )}

      {collectionType === COUNT_TABLE && (
        <div className='collect-count'>
          {chooserState.selectedForm.fieldsets.map((fs) => (
            <div key={'fs-' + fs.fieldset.id}>
              <hr />
              <strong>{fs.fieldset.name}</strong>
              <ul className='orderable'>
                {fs.fields.filter((f) => OPT_TYPES.includes(f.type)).length ? (
                  fs.fields
                    .filter((f) => OPT_TYPES.includes(f.type))
                    .map((f) => (
                      <React.Fragment key={'count-' + f.id}>
                        <li
                          onClick={() => {
                            let token = f.id.toString() + '.value';
                            buildInsertString(COUNT_TABLE, [
                              { token, style: '' },
                            ]);
                          }}
                          title='Insert token'
                        >
                          {f.name}
                        </li>
                        {chooserState.selectedForm.metadata_entries
                          .filter((me) => me.field_id == f.id)
                          .map((me) => (
                            <li
                              key={`${f.id}-${me.id}`}
                              onClick={() => {
                                let token = f.id.toString() + '.' + me.title;
                                buildInsertString(COUNT_TABLE, [
                                  { token, style: '' },
                                ]);
                              }}
                            >
                              {f.name} - {me.title}
                            </li>
                          ))}
                      </React.Fragment>
                    ))
                ) : (
                  <span>No option type fields...</span>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionChooser;
